// src/services/bunnyStream.js
const BUNNY_LIBRARY_ID = process.env.REACT_APP_BUNNY_LIBRARY_ID;

const THUMBNAIL_MAP = {
    'upper': '💪', 'lower': '🦵', 'core': '🔥', 'stretch': '🧘',
    'upper-core': '💥', 'lower-core': '🌸', 'upper-lower': '⚡',
    'upper-lower-core': '🌟',
};

/**
 * Parse a video title to extract duration, muscle groups, and display name.
 *
 * Supports TWO formats:
 * 1. Underscore format: "10_upper-lower_Burn and Sculpt"
 * 2. Natural titles: "[Program Name] Video 6: 10 Minute Upper & Lower Body"
 */
export function parseVideoTitle(title, videoId) {
    const cleaned = title.replace(/\.(mp4|mov|webm|avi|mkv)$/i, '');
    const parts = cleaned.split('_');
    if (parts.length >= 3) {
        const length = parseInt(parts[0], 10);
        const validGroups = ['upper', 'lower', 'core', 'stretch'];
        const groups = parts[1].toLowerCase().split('-').filter((g) => validGroups.includes(g));
        if (!isNaN(length) && length > 0 && groups.length > 0) {
            const titleText = parts.slice(2).join(' ')
                .replace(/([a-z])([A-Z])/g, '$1 $2')
                .replace(/([a-zA-Z])(\d)/g, '$1 $2').trim();
            return buildVideo(videoId, titleText, length, groups);
        }
    }

    // Natural title parsing
    const minMatch = cleaned.match(/(\d+)\s*[-–]?\s*min(?:ute)?s?\b/i);
    const length = minMatch ? parseInt(minMatch[1], 10) : 0;

    const lowerTitle = cleaned.toLowerCase();
    const groups = [];
    if (/\bupper\b/.test(lowerTitle)) groups.push('upper');
    if (/\blower\b/.test(lowerTitle)) groups.push('lower');
    if (/\bcore\b/.test(lowerTitle)) groups.push('core');
    if (/\bstretch\b|\bstretching\b|\bflexibility\b|\bmobility\b/.test(lowerTitle)) groups.push('stretch');
    if (groups.length === 0) {
        if (/\barms?\b|\bshoulder|bicep|tricep|chest|back\b|\bbatwing/i.test(lowerTitle)) groups.push('upper');
        if (/\blegs?\b|\bglute|squat|lunge|calf|calves|hamstring|quad\b/i.test(lowerTitle)) groups.push('lower');
        if (/\babs?\b|\bplank|oblique\b/i.test(lowerTitle)) groups.push('core');
        if (/\byoga\b|\bcool\s*down\b|\bwarm\s*up\b|\brecovery\b/i.test(lowerTitle)) groups.push('stretch');
    }

    let displayTitle = cleaned.replace(/^\[.*?\]\s*/, '').trim();
    displayTitle = displayTitle.replace(/^Video\s+\d+\s*[:\-–]\s*/i, '').trim();

    if (length > 0 || groups.length > 0) {
        return buildVideo(videoId, displayTitle || cleaned, length, groups);
    }
    return buildVideo(videoId, displayTitle || cleaned, 0, []);
}

function buildVideo(videoId, title, length, groups) {
    const groupKey = groups.sort().join('-');
    const thumbnail = THUMBNAIL_MAP[groupKey] || '✨';
    const groupLabels = groups.map((g) => g === 'upper' ? 'upper body' : g === 'lower' ? 'lower body' : g);
    const desc = length > 0 && groups.length > 0
        ? `${length}-minute ${groupLabels.join(' & ')} workout.`
        : length > 0
            ? `${length}-minute workout.`
            : groups.length > 0
                ? `${groupLabels.join(' & ')} workout.`
                : '';

    const cdnHost = process.env.REACT_APP_BUNNY_CDN_HOSTNAME;

    return {
        id: videoId,
        title,
        length: length || 0,
        groups,
        thumbnail,
        description: desc,
        // Bunny Stream embed URL — clean player, no branding
        streamUrl: `https://iframe.mediadelivery.net/embed/${BUNNY_LIBRARY_ID}/${videoId}?autoplay=true&loop=false&muted=false&preload=true&responsive=true`,
        bunnyUrl: `https://iframe.mediadelivery.net/play/${BUNNY_LIBRARY_ID}/${videoId}`,
        // Direct HLS URL for native <video> playback (enables AirPlay + Chromecast)
        hlsUrl: cdnHost ? `https://${cdnHost}/${videoId}/playlist.m3u8` : null,
    };
}

/**
 * Fetch all videos from a Bunny Stream collection.
 * @param {string} collectionId - Bunny Stream collection GUID
 * @returns {Promise<object[]|null>}
 */
export async function fetchVideosFromCollection(collectionId) {
    if (!collectionId || !BUNNY_LIBRARY_ID) return null;
    // Retry transient failures with short backoff. A single failed attempt used to
    // return null, which every caller coerces to [] — surfacing a PURCHASED program
    // as "No videos uploaded yet". Root cause of the intermittent-empty-program
    // reports: Bunny API rate-limits (429), Vercel serverless cold-start/timeout on
    // the paginated proxy, or a network blip. Retrying absorbs those so a one-off
    // hiccup never reaches the user. Contract is unchanged: array on success (happy
    // path is byte-identical — first attempt returns), null only after exhausting
    // retries; never throws.
    const MAX_ATTEMPTS = 3;
    for (let attempt = 1; attempt <= MAX_ATTEMPTS; attempt++) {
        try {
            // Fetch video list via server-side proxy (API key stays server-side)
            const response = await fetch(`/api/bunny-videos?collection=${encodeURIComponent(collectionId)}`);
            if (!response.ok) throw new Error(`bunny-videos ${response.status}`);
            const data = await response.json();
            const allVideos = data.items || [];

            return allVideos
                .filter((v) => v.status >= 3) // Only show encoded/ready videos
                .map((v) => {
                    const parsed = parseVideoTitle(v.title, v.guid);
                    if (parsed) {
                        // Use thumbnail from Bunny CDN — use thumbnailFileName for custom thumbnails
                        const cdnHost = process.env.REACT_APP_BUNNY_CDN_HOSTNAME;
                        if (cdnHost) {
                            const thumbFile = v.thumbnailFileName || 'thumbnail.jpg';
                            parsed.driveThumbnail = `https://${cdnHost}/${v.guid}/${thumbFile}`;
                        }
                        parsed.createdTime = v.dateUploaded;
                        // Use Bunny's reported length if our parser didn't find one
                        if (!parsed.length && v.length) {
                            parsed.length = Math.round(v.length / 60);
                        }
                    }
                    return parsed;
                }).filter(Boolean);
        } catch (err) {
            if (attempt >= MAX_ATTEMPTS) {
                console.error('Error fetching Bunny Stream collection:', err);
                return null;
            }
            // Backoff: 400ms, then 800ms before the final attempt.
            await new Promise((resolve) => setTimeout(resolve, attempt * 400));
        }
    }
    return null;
}

// Convenience: fetch free workouts collection
export async function fetchFreeVideos() {
    const collectionId = process.env.REACT_APP_BUNNY_FREE_COLLECTION_ID;
    return fetchVideosFromCollection(collectionId);
}

// ─── Program images from Bunny Storage (via API proxy) ───
const BUNNY_STORAGE_CDN = process.env.REACT_APP_BUNNY_STORAGE_CDN;

export async function fetchImagesFromFolder(folderPath) {
    if (!BUNNY_STORAGE_CDN) return null;
    try {
        const response = await fetch(`/api/bunny-images?folder=${encodeURIComponent(folderPath)}`);
        if (!response.ok) return null;
        const files = await response.json();
        return files
            .filter((f) => !f.IsDirectory && /\.(jpg|jpeg|png|webp|gif|bmp)$/i.test(f.ObjectName))
            .map((f) => {
                const nameWithoutExt = f.ObjectName.replace(/\.(jpg|jpeg|png|webp|gif|bmp)$/i, '');
                const title = nameWithoutExt.replace(/[_-]/g, ' ').replace(/([a-z])([A-Z])/g, '$1 $2').trim();
                const cdnUrl = `https://${BUNNY_STORAGE_CDN}/${folderPath}/${f.ObjectName}`;
                return {
                    id: f.Guid,
                    name: f.ObjectName,
                    title,
                    thumbnailUrl: cdnUrl,
                    imageUrl: cdnUrl,
                    createdTime: f.DateCreated,
                };
            });
    } catch (err) { console.error('Error fetching Bunny Storage images:', err); return null; }
}

export async function fetchProgramImages() {
    return fetchImagesFromFolder('Program Images');
}